import React, { Fragment, memo, useMemo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, object, oneOfType, shape, string } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { getVariationDetails } from './getVariationDetails'
import useVisibleCount from './useVisibleCount'

const valueClassName = index => classnames(
  'variation-card__info-value',
  index === 0 && 'variation-card__info-value--primary',
)

const renderValues = (details, itemProps = {}) => details.map(({ label, value, shortValue }, index) => (
  <Fragment key={label}>
    {index > 0 && <span className="variation-card__info-dot" aria-hidden="true" />}
    <span className={valueClassName(index)} {...itemProps}>{shortValue ?? value}</span>
  </Fragment>
))

function Info({ variation }) {
  const t = useTranslation()
  const details = useMemo(() => getVariationDetails(variation, t), [variation, t])
  const lines = details.map(({ label, value }) => `${label}: ${value}`)
  const [rowRef, measureRef, visibleCount] = useVisibleCount(details.length, lines.join('|'))
  const hiddenCount = details.length - visibleCount

  if (details.length === 0) {
    return null
  }

  return (
    <>
      <DesktopOnlyTooltip
        id={`variation-info-${variation.id}`}
        content={lines.map(line => <span key={line} className="variation-card__info-line">{line}</span>)}
        place="top"
        containerClass="variation-card__info"
        className="variation-card__info-trigger"
        focusable
        ariaLabel={lines.join(', ')}
      >
        <span ref={rowRef} className="variation-card__info-row cgt-caption">
          {renderValues(details.slice(0, visibleCount))}
          {hiddenCount > 0 && (
            <>
              <span className="variation-card__info-dot" aria-hidden="true" />
              <span className="variation-card__info-more">+{hiddenCount}</span>
            </>
          )}
          <span ref={measureRef} className="variation-card__info-measure" aria-hidden="true">
            {renderValues(details, { 'data-measure-item': true })}
            <span className="variation-card__info-dot" />
            <span className="variation-card__info-more" data-measure-counter>+{details.length}</span>
          </span>
        </span>
      </DesktopOnlyTooltip>
      {/* Below sm there is no hover to reveal the tooltip, so the stylesheet swaps
          the row for the full list. */}
      <ul className="variation-card__info-list cgt-caption">
        {lines.map(line => <li key={line} className="variation-card__info-list-item">{line}</li>)}
      </ul>
    </>
  )
}

Info.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    providerModelId: string,
    providerRunAttributes: object,
  }).isRequired,
}

export default memo(Info)
